import type { KeyboardEvent } from 'react';

/**
 * Tipkovnica za `role="radiogroup"`.
 *
 * Skupina radio gumba je JEDNA postaja tabulatora; unutar nje se kreće
 * strelicama, a pomak odmah mijenja odabir (WAI-ARIA, obrazac „radio group").
 * Bez ovoga je korisnik tipkovnice morao pretabulirati svih pet ocjena
 * check-ina i šest filtara edukacije da dođe do sadržaja iza njih.
 *
 * Vraća indeks koji treba odabrati i fokusirati, ili `null` ako tipka nije naša.
 */
export function nextRadioIndex(key: string, current: number, count: number): number | null {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return (current + 1) % count;
    case 'ArrowLeft':
    case 'ArrowUp':
      return (current - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/**
 * Zajednički rukovatelj: pomiče odabir i fokus na susjedni gumb u istoj skupini.
 * Gumbi se traže u DOM-u pod `[role="radio"]`, pa komponenta ne mora držati refove.
 */
export function handleRadioGroupKey<T>(
  event: KeyboardEvent<HTMLElement>,
  currentIndex: number,
  values: readonly T[],
  onChange: (value: T) => void,
): void {
  const next = nextRadioIndex(event.key, currentIndex, values.length);
  if (next === null) return;
  event.preventDefault();
  const target = values[next];
  if (target === undefined) return;
  onChange(target);
  const group = event.currentTarget.closest('[role="radiogroup"]');
  const buttons = group?.querySelectorAll<HTMLElement>('[role="radio"]');
  buttons?.[next]?.focus();
}
